import React, { useState, useContext } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Tabs,
  Tab,
  Input,
  Textarea,
  DatePicker,
  Select,
  SelectItem,
} from '@heroui/react';
import { BackendContext } from '../Context/BackendContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import { FileUploader } from '@aws-amplify/ui-react-storage';

const SHAPES = [
  // o
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],
  [[1]],

  //i
  [[1, 1]],
  [[1], [1]],
  [[1, 1]],
  [[1], [1]],
  [[1, 1]],
  [[1], [1]],
  [[1, 1]],
  [[1], [1]],
  [[1, 1]],
  [[1], [1]],
  
  // l
  [[1, 1], [0, 1]],
  [[1, 1], [1, 0]],
  [[0, 1], [1, 1]],
  [[1, 0], [1, 1]],

  // O
  [[1, 1], [1, 1]],
  [[1, 1], [1, 1]],
  [[1, 1], [1, 1]],
  [[1, 1], [1, 1]],

  // I
  [[1], [1], [1], [1]],
  [[1, 1, 1, 1]],

  // T
  //[[1, 1, 1], [0, 1, 0]],
  //[[1, 0], [1, 1], [1, 0]],

  // L
  //[[1, 0], [1, 0], [1, 1]],
  //[[1, 1, 1], [0, 0, 1]],

  // Z
  //[[1, 1, 0], [0, 1, 1]],
  //[[0, 1], [1, 1], [1, 0]],
];

function getRandomizedShapes() {
  const shapes = [...SHAPES];
  for (let i = shapes.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shapes[i], shapes[j]] = [shapes[j], shapes[i]];
  }
  return shapes;
}

function generateTilingWithRetries(X, Y, maxTries = 10) {
  for (let i = 0; i < maxTries; i++) {
    const result = generateTiling(X, Y); // your original function with timeout
    if (result) return result;
  }
  throw new Error("Failed to generate a full tiling after multiple attempts.");
}

function generateTiling(X, Y) {
  const grid = Array.from({ length: Y }, () => Array(X).fill(0));
  let tileId = 1;

  function canPlace(shape, r, c) {
    for (let i = 0; i < shape.length; i++) {
      for (let j = 0; j < shape[0].length; j++) {
        if (shape[i][j]) {
          const y = r + i;
          const x = c + j;
          if (y >= Y || x >= X || grid[y][x] !== 0) return false;
        }
      }
    }
    return true;
  }

  function placeShape(shape, r, c, id) {
    for (let i = 0; i < shape.length; i++) {
      for (let j = 0; j < shape[0].length; j++) {
        if (shape[i][j]) grid[r + i][c + j] = id;
      }
    }
  }

  function removeShape(shape, r, c) {
    for (let i = 0; i < shape.length; i++) {
      for (let j = 0; j < shape[0].length; j++) {
        if (shape[i][j]) grid[r + i][c + j] = 0;
      }
    }
  }
  let attemptCount = 0;
  const MAX_ATTEMPTS = 50000; // reasonable cap

  function backtrack() {
    attemptCount++;
    if (attemptCount > MAX_ATTEMPTS) return false;
    for (let r = 0; r < Y; r++) {
      for (let c = 0; c < X; c++) {
        if (grid[r][c] === 0) {
          for (const shape of getRandomizedShapes()) {
            if (canPlace(shape, r, c)) {
              placeShape(shape, r, c, tileId++);
              if (backtrack()) return true;
              tileId--;
              removeShape(shape, r, c);
            }
          }
          return false; // no valid placement
        }
      }
    }
    return true; // grid completely filled
  }

  const success = backtrack();
  return success ? grid : null;
}

function drawTileGridOverlay(canvas, grid, cellSize = 32) {
  const ctx = canvas.getContext("2d");
  const rows = grid.length;
  const cols = grid[0].length;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Optional: draw tiles with colors for debugging
  const colors = {};
  function getRandomColor(id) {
    if (!colors[id]) {
      colors[id] = `hsl(${(id * 137) % 360}, 70%, 60%)`;
    }
    return colors[id];
  }

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const id = grid[y][x];
      ctx.fillStyle = 'beige';
      ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
    }
  }

  // Draw grid overlay between different tiles
  ctx.strokeStyle = "black";
  ctx.lineWidth = 2;

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const id = grid[y][x];

      // Check adjacent cells to draw borders
      const neighbors = [
        [y - 1, x], // top
        [y + 1, x], // bottom
        [y, x - 1], // left
        [y, x + 1]  // right
      ];

      neighbors.forEach(([ny, nx]) => {
        if (
          ny < 0 || ny >= rows ||
          nx < 0 || nx >= cols ||
          grid[ny][nx] !== id
        ) {
          // draw border between different tiles or edges
          if (ny === y - 1 && nx === x) {
            // top
            ctx.beginPath();
            ctx.moveTo(x * cellSize, y * cellSize);
            ctx.lineTo((x + 1) * cellSize, y * cellSize);
            ctx.stroke();
          } else if (ny === y + 1 && nx === x) {
            // bottom
            ctx.beginPath();
            ctx.moveTo(x * cellSize, (y + 1) * cellSize);
            ctx.lineTo((x + 1) * cellSize, (y + 1) * cellSize);
            ctx.stroke();
          } else if (ny === y && nx === x - 1) {
            // left
            ctx.beginPath();
            ctx.moveTo(x * cellSize, y * cellSize);
            ctx.lineTo(x * cellSize, (y + 1) * cellSize);
            ctx.stroke();
          } else if (ny === y && nx === x + 1) {
            // right
            ctx.beginPath();
            ctx.moveTo((x + 1) * cellSize, y * cellSize);
            ctx.lineTo((x + 1) * cellSize, (y + 1) * cellSize);
            ctx.stroke();
          }
        }
      });
    }
  }
}

export default function AdminModal({ isOpen, onClose }) {
  const backendContext = useContext(BackendContext);

  const [selectedTab, setSelectedTab] = useState("createCanvas");

  // Canvas form state
  const [canvasPage, setCanvasPage] = useState('');
  const [canvasPrice, setCanvasPrice] = useState('');
  const [canvasSize, setCanvasSize] = useState('');
  const [canvasShape, setCanvasShape] = useState('');
  const [canvasDimX, setCanvasDimX] = useState('');
  const [canvasDimY, setCanvasDimY] = useState('');
  const [resizePage, setResizePage] = useState('');
  const [resizeDimension, setResizeDimension] = useState('512');
  const [adminMessage, setAdminMessage] = useState('');

  // Event form state
  const [eventTheme, setEventTheme] = useState('');
  const [eventPage, setEventPage] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventStartTime, setEventStartTime] = useState(null);
  const [eventEndTime, setEventEndTime] = useState(null);

  // News form state
  const [newsTitle, setNewsTitle] = useState('');
  const [newsDescription, setNewsDescription] = useState('');
  const [newsContent, setNewsContent] = useState('');

  const handleSubmit = async () => {
    setAdminMessage('');
    if(selectedTab === 'createCanvas') {
      console.log({ canvasPage, canvasPrice, canvasSize, canvasShape, canvasDimX, canvasDimY });
      
      //await backendContext.createCanvas(canvasPage, canvasPrice, canvasSize, canvasShape, canvasDimX, canvasDimY);

    } else if(selectedTab === 'uploadS3File') {
      console.log({ canvasPage, canvasPrice, canvasSize, canvasDimX, canvasDimY });
      
      //await backendContext.createEventCanvas(canvasPage, canvasPrice, canvasSize, canvasDimX, canvasDimY);

    } else if (selectedTab === 'playWithNewCanvas') {
      const canvas = document.getElementById("myCanvas")
      const tiling = generateTilingWithRetries(32, 18);
      drawTileGridOverlay(canvas, tiling, 32);
      console.log(tiling);
    } else if (selectedTab === 'createEvent') {    
      const startUTC = new Date(eventStartTime).toISOString();     // e.g. "2025-07-21T19:00:00.000Z"
      const endUTC = new Date(eventEndTime).toISOString();

      console.log({
        eventTheme,
        eventPage,
        eventType,
        eventDescription,
        startUTC,
        endUTC,
      });

      //await backendContext.createEvent(startUTC, endUTC, eventTheme, eventType, eventDescription);

    } else if (selectedTab === 'createNews') {
      console.log({ newsTitle, newsDescription, newsContent });
      await confirmAuthSession();

      await backendContext.createNews(newsTitle, newsDescription, newsContent);
    } else if (selectedTab === 'resizeCanvas') {
      const nextDimension = Number(resizeDimension);
      const result = await backendContext.updateCanvasDimensions(
        resizePage,
        nextDimension,
        nextDimension,
      );
      setAdminMessage(result?.message || 'No response from canvas resize.');
    }
    //onClose(); // or remove this to stay open after submission
  };

  const confirmAuthSession = async () => {
    const session = await fetchAuthSession();
    console.log("Identity Pool ID:", session.identityId); // ✅ will be undefined if using userPool only
    console.log("Tokens:", session.tokens?.idToken?.payload);

    const groups = session.tokens?.idToken?.payload['cognito:groups'];
    console.log("Groups:", groups);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      backdrop="blur"
      isDismissable={false}
      classNames={{
        wrapper: 'z-[1200]',
        base: 'bg-gray-800 z-[1200] w-[90vw] max-w-4xl',
      }}
    >
      <ModalContent className="z-[1200]">
        <ModalHeader className="text-lg font-bold text-white">
          Admin Panel
        </ModalHeader>

        <ModalBody>
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={setSelectedTab}
            color="primary"
            variant="underlined"
            fullWidth
          >
            <Tab key="playWithNewCanvas" title="Experiment Canvas">
              <canvas id="myCanvas" width="1024" height="576"></canvas>
            </Tab>
            <Tab key="createCanvas" title="Create Canvas">
              <div className="space-y-4 mt-4">
                <Input
                  label="Page"
                  value={canvasPage}
                  onChange={(e) => setCanvasPage(e.target.value)}
                />
                <Input
                  label="Price (tokens/hour)"
                  type="number"
                  value={canvasPrice}
                  onChange={(e) => setCanvasPrice(e.target.value)}
                />
                <Input
                  label="Size"
                  type="number"
                  value={canvasSize}
                  onChange={(e) => setCanvasSize(e.target.value)}
                />
                <Input
                  label="Shape"
                  value={canvasShape}
                  onChange={(e) => setCanvasShape(e.target.value)}
                />
                <Input
                  label="DimX"
                  type="number"
                  value={canvasDimX}
                  onChange={(e) => setCanvasDimX(e.target.value)}
                />
                <Input
                  label="DimY"
                  type="number"
                  value={canvasDimY}
                  onChange={(e) => setCanvasDimY(e.target.value)}
                />
              </div>
            </Tab>

            <Tab key="resizeCanvas" title="Canvas Size">
              <div className="space-y-4 mt-4">
                <Input
                  label="Page"
                  placeholder="A"
                  value={resizePage}
                  onChange={(e) => setResizePage(e.target.value.toUpperCase())}
                />
                <Select
                  label="Canvas Size"
                  selectedKeys={[resizeDimension]}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0];
                    if (value) setResizeDimension(String(value));
                  }}
                >
                  {['512', '1024'].map((size) => (
                    <SelectItem key={size} value={size}>
                      {`${size} x ${size}`}
                    </SelectItem>
                  ))}
                </Select>
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-3 text-sm text-slate-300">
                  This updates the shared canvas record for the page, so the new size applies to both
                  ` PlaceCanvas ` and ` Canvas ` for that page.
                </div>
                {adminMessage && (
                  <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-3 text-sm text-slate-300">
                    {adminMessage}
                  </div>
                )}
              </div>
            </Tab>

            <Tab key="uploadS3File" title="Upload To S3">
              <div className="space-y-4 mt-4">
                <Input
                  label="Page"
                  value={canvasPage}
                  onChange={(e) => setCanvasPage(e.target.value)}
                />
                <FileUploader
                  acceptedFileTypes={['image/*']}
                  path="general/tutorial/"
                  maxFileCount={1}
                  isResumable
                />
              </div>
            </Tab>

            <Tab key="createEvent" title="Create Event">
              <div className="space-y-4 mt-4">
                <Input
                  label="Theme"
                  value={eventTheme}
                  onChange={(e) => setEventTheme(e.target.value)}
                />
                <Input
                  label="Page"
                  value={eventPage}
                  onChange={(e) => setEventPage(e.target.value)}
                />
                <Input
                  label="Type"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                />
                <Textarea
                  label="Description"
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                />
                <DatePicker
                  label="Start Time"
                  value={eventStartTime}
                  onChange={setEventStartTime}
                />
                <DatePicker
                  label="End Time"
                  value={eventEndTime}
                  onChange={setEventEndTime}
                />
              </div>
            </Tab>

            <Tab key="createNews" title="Create News">
              <div className="space-y-4 mt-4">
                <Input
                  label="Title"
                  value={newsTitle}
                  onChange={(e) => setNewsTitle(e.target.value)}
                />
                <Textarea
                  label="Description"
                  value={newsDescription}
                  onChange={(e) => setNewsDescription(e.target.value)}
                />
                <Textarea
                  label="Content"
                  value={newsContent}
                  onChange={(e) => setNewsContent(e.target.value)}
                />
              </div>
            </Tab>
          </Tabs>
        </ModalBody>

        <ModalFooter className="flex justify-end space-x-3">
          <Button onPress={onClose} variant="light" className="text-white border border-gray-500">
            Cancel
          </Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onPress={handleSubmit}
          >
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
